import { FormShowTemplate } from 'framework/form';
import { ProjectSecurityContext } from 'domain/shared/newProject';
import { useParams } from 'react-router-dom';
import useRiskService from '../useRiskService';
import { useRiskTimeline } from '../hooks/useRiskTimeline';
import { Loading } from 'framework/ui/mui-builder';
import MainRiskSection from './MainRiskSection';
import RiskTimelineLayout from '../timeline/RiskTimelineLayout';
import { Stack, Typography } from '@mui/material';
import { DATE_FORMAT, useDateTimeService } from 'framework/utils';
import RiskBreadcrumbs from './RiskBreadcrumbs';
import { ShowPageLayout } from 'framework/ui/layout';

const ViewRiskLayout = () => {
  const { getRisksValidationSchema } = useRiskService();
  const { id, riskId } = useParams();
  const {
    data: risk,
    projectName,
    permissions,
    loading,
    refetch,
    getTimelineItems,
    getTimelinePageById,
    createTimelineItem,
    pinTimelineAction,
  } = useRiskTimeline(id!, riskId!);
  const { formatDateWithTimezone } = useDateTimeService();
  const { updateRisk } = useRiskService();

  if (loading && !risk) {
    return <Loading />;
  }
  if (!risk) {
    return null;
  }

  return (
    <ProjectSecurityContext permissions={permissions}>
      <ShowPageLayout
        breadcrumbsButtons={[
          <Stack direction="column">
            {risk?.createdAt ? (
              <Typography variant="body2" color="textSecondary" fontStyle="italic">
                Created {formatDateWithTimezone(risk?.createdAt, DATE_FORMAT.FULL)}
              </Typography>
            ) : null}
            {risk?.updatedAt ? (
              <Typography variant="body2" color="textSecondary" fontStyle="italic">
                Last modified {formatDateWithTimezone(risk?.updatedAt, DATE_FORMAT.FULL)}
              </Typography>
            ) : null}
          </Stack>,
        ]}
        breadcrumbs={<RiskBreadcrumbs risk={risk!} projectName={projectName} />}
        hasSeparateSections
      >
        <FormShowTemplate
          isCreateRequest={false}
          initialData={() => risk}
          getDataFunc={() =>
            Promise.resolve({
              id: risk?.id,
              alias: risk?.alias,
              projectId: risk?.projectId,
              createdBy: risk?.createdBy,
              title: risk?.title,
              description: risk?.description,
              type: risk?.type,
              probability: risk?.probability,
              severity: risk?.severity,
              riskOwner: risk?.riskOwner,
              status: risk?.status,
              resolvedAt: risk?.resolvedAt,
              resolveComment: risk?.resolveComment,
              mitigationStrategy: risk?.mitigationStrategy,
              isIncludedInCustomerReport: risk?.isIncludedInCustomerReport,
              timeline: risk?.timeline || [],
              technicalNote: '',
              isEscalated: risk?.isEscalated,
            })
          }
          submitFormFunc={values => {
            return updateRisk(riskId!, {
              id: values?.id,
              title: values?.title,
              description: values?.description,
              type: values?.type,
              probability: values?.probability,
              severity: values?.severity,
              riskOwner: values?.riskOwner,
              status: values?.status,
              resolvedAt: values?.resolvedAt,
              resolveComment: values?.resolveComment,
              mitigationStrategy: values?.mitigationStrategy,
              isIncludedInCustomerReport: values?.isIncludedInCustomerReport,
            });
          }}
          afterSubmit={{
            message: 'You are awesome! The Risk has been successfully updated.',
            execute: () => {
              refetch();
            },
          }}
          getValidationSchemaFunc={getRisksValidationSchema}
        >
          <>
            <MainRiskSection onEscalationUpdated={refetch} />
            <RiskTimelineLayout
              loading={loading}
              refetch={refetch}
              getTimelineItems={getTimelineItems}
              getTimelinePageById={getTimelinePageById}
              createTimelineItem={createTimelineItem}
              pinTimelineAction={pinTimelineAction}
            />
          </>
        </FormShowTemplate>
      </ShowPageLayout>
    </ProjectSecurityContext>
  );
};

export default ViewRiskLayout;
